// =============================================================================
// MessageBubble — one row in the message list.
//
// Renders text / photo / voice / system / ai_action with brand-correct
// typography and grammar:
//   - SEVRIC AI gets the hex-mark avatar + AI badge
//   - Human teammates get colored initials avatars
//   - Photos render in editorial frames
//   - Voice notes show a waveform + transcript
//   - System events render as cobalt pills (charge started, intake, etc.)
// =============================================================================
import React from "react";
import { authorName, authorInitials, isSevricAI } from "../hooks.js";
import { SevricMark } from "./SevricMark.jsx";

const HUMAN_PALETTE = ["#946A12", "#2A6D38", "#7C2935", "#2E5694", "#4A7BC8"];

function colorForUid(uid) {
  if (!uid) return HUMAN_PALETTE[0];
  let h = 0;
  for (let i = 0; i < uid.length; i++) h = (h * 31 + uid.charCodeAt(i)) >>> 0;
  return HUMAN_PALETTE[h % HUMAN_PALETTE.length];
}

function relativeWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const ms = d.getTime();
  const diff = Date.now() - ms;
  const m = Math.floor(diff / 60000);
  if (m < 1)  return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  const d2 = Math.floor(h / 24);
  if (d2 < 7) return `${d2}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function MessageBubble({ msg }) {
  const isAi = isSevricAI(msg);
  const isSystem = msg.message_kind === "system";
  if (isSystem) return <SystemBubble msg={msg} />;

  const name = authorName(msg);
  const initials = authorInitials(msg);
  return (
    <div className={`chat-msg${isAi ? " ai-msg" : ""}`}>
      <div className={`chat-msg-avi${isAi ? " ai" : ""}`} style={!isAi ? { background: colorForUid(msg.author_uid) } : undefined}>
        {isAi ? <SevricMark size={36} /> : initials}
      </div>
      <div className="chat-msg-body">
        <div className="chat-msg-head">
          <span className="chat-msg-name">
            {isAi ? "SEVRIC AI" : name}
            {isAi && (
              <span className="chat-msg-ai-badge">
                <span className="pulse" /> AI
              </span>
            )}
          </span>
          <span className="chat-msg-time">{relativeWhen(msg.created_at)}</span>
        </div>
        {msg.body && <div className="chat-msg-text">{renderBody(msg.body, msg.mentions)}</div>}
        {Array.isArray(msg.attachments) && msg.attachments.map((a, i) => (
          <Attachment key={i} att={a} />
        ))}
        {Array.isArray(msg.ai_meta?.suggested_actions) && (
          <div className="chat-msg-actions">
            {msg.ai_meta.suggested_actions.map((act, i) => (
              <button key={i} type="button" className={`chat-msg-btn${act.style === "ghost" ? " ghost" : ""}`}>
                {act.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function renderBody(text, mentions) {
  // Inline @mentions get the cobalt chip treatment. mentions[] is a list of
  // uids; we look for the literal `@uid` token in the body and replace.
  if (!Array.isArray(mentions) || mentions.length === 0) {
    return <>{text}</>;
  }
  const parts = [];
  let rest = text;
  const re = /@([a-zA-Z0-9_-]+)/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const isAi = m[1] === "sevric-ai" || m[1].toLowerCase() === "sevric";
    parts.push(
      <span key={m.index} className={`chat-msg-mention${isAi ? " ai" : ""}`}>
        @{isAi ? "sevric" : m[1]}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function Attachment({ att }) {
  if (att?.type === "photo") {
    return (
      <div className="chat-msg-photo">
        <img src={att.url} alt={att.alt || ""} width={att.width || undefined} height={att.height || undefined} />
        {att.caption && <div className="chat-msg-photo-cap">{att.caption}</div>}
      </div>
    );
  }
  if (att?.type === "voice") {
    return (
      <div className="chat-msg-voice">
        <button type="button" className="chat-msg-voice-play" aria-label="Play voice note">
          <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12"><polygon points="5 3 19 12 5 21 5 3"/></svg>
        </button>
        <Waveform />
        <span className="chat-msg-voice-dur">{formatDur(att.duration_ms)}</span>
        {att.transcript && <div className="chat-msg-voice-transcript">{`“${att.transcript}”`}</div>}
      </div>
    );
  }
  return null;
}

function Waveform() {
  // Static waveform bars — purely decorative until we wire the actual audio.
  const bars = Array.from({ length: 32 }, (_, i) => 4 + ((i * 13) % 18));
  return (
    <div className="chat-msg-voice-wave" aria-hidden="true">
      {bars.map((h, i) => <span key={i} style={{ height: h + "px", opacity: 0.4 + (i % 5) * 0.12 }} />)}
    </div>
  );
}

function formatDur(ms) {
  if (!ms || !Number.isFinite(ms)) return "0:00";
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

// =============================================================================
// SystemBubble — charge started, intake logged, etc. Cobalt pill, never
// looks like a chat message — they're status pings, not conversation.
// =============================================================================
function SystemBubble({ msg }) {
  const meta = msg.ai_meta?.system || {};
  return (
    <div className="chat-sys-event">
      <span className="chat-sys-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
          {meta.icon === "intake" ? <polyline points="20 6 9 17 4 12" /> : <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />}
        </svg>
      </span>
      <span>
        {msg.body && <b>{msg.body}</b>}
        {meta.detail && <span className="chat-sys-meta"> · {meta.detail}</span>}
      </span>
    </div>
  );
}
